import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPaymentInfo,
  initiateGuestPayment,
  formatAmount,
} from '../services/guestPaymentService';
import './GuestPayment.css';

// Firefox has issues with Paymob iframe 3DS redirects
const isFirefox = () => navigator.userAgent.toLowerCase().includes('firefox');

const GuestPayment = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [selectedGuestCount, setSelectedGuestCount] = useState(1);
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  // Check if this is a per-reservation payment (not per-guest)
  const isPerReservation = paymentInfo?.paymentType === 'per_reservation';

  useEffect(() => {
    fetchPaymentInfo();
  }, [token]);

  const fetchPaymentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPaymentInfo(token);
      setPaymentInfo(data);

      // Set default guest count to remaining guests or 1
      if (data.remainingGuests > 0) {
        setSelectedGuestCount(Math.min(1, data.remainingGuests));
      }
    } catch (err) {
      console.error('Failed to fetch payment info', err);
      setError(err.response?.data?.message || 'Invalid payment link or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError(null);

      // For per-reservation, use totalGuests; for per-guest, use selectedGuestCount
      const guestCount = isPerReservation ? paymentInfo.totalGuests : selectedGuestCount;

      const response = await initiateGuestPayment(token, {
        guestCount,
        payerName: payerName || undefined,
        payerEmail: payerEmail || undefined,
        payerPhone: payerPhone || undefined,
      });

      // Firefox has issues with 3DS redirects in iframes - use full page redirect
      if (isFirefox()) {
        window.location.href = response.paymentUrl;
        return;
      }

      // Other browsers: use iframe as before
      setIframeUrl(response.paymentUrl);
      setShowIframe(true);
    } catch (err) {
      console.error('Failed to initiate payment', err);
      setError(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const calculateTotal = () => {
    if (!paymentInfo) return 0;
    // For per-reservation, return the remaining amount; for per-guest, calculate by guest count
    if (isPerReservation) {
      return paymentInfo.remainingAmountCents;
    }
    return selectedGuestCount * paymentInfo.pricePerGuestCents;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="guest-payment-container">
        <div className="loading-state">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (error && !paymentInfo) {
    return (
      <div className="guest-payment-container">
        <div className="error-state">
          <i className="bi bi-exclamation-circle"></i>
          <h2>Payment Link Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (showIframe) {
    return (
      <div className="guest-payment-container">
        <div className="iframe-container">
          <div className="iframe-header">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setShowIframe(false);
                fetchPaymentInfo(); // Refresh payment info
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
            <h5>Complete Your Payment</h5>
          </div>
          <div className="payment-iframe-wrapper">
            <iframe
              src={iframeUrl}
              title="Paymob Payment"
              className="payment-iframe"
              allow="payment"
            />
          </div>
          <div className="iframe-footer">
            <div className="payment-secure-info">
              <i className="bi bi-shield-lock-fill"></i>
              Your payment is secured by Paymob
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentInfo?.isFullyPaid) {
    return (
      <div className="guest-payment-container">
        <div className="success-state">
          <i className="bi bi-check-circle-fill"></i>
          <h2>Payment Complete!</h2>
          <p>This reservation has been fully paid.</p>
          <div className="reservation-summary">
            <div className="summary-item">
              <span className="label">Restaurant</span>
              <span className="value">{paymentInfo.restaurantName}</span>
            </div>
            <div className="summary-item">
              <span className="label">Date</span>
              <span className="value">{formatDate(paymentInfo.reservationDate)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Time</span>
              <span className="value">{formatTime(paymentInfo.reservationTime)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Guests</span>
              <span className="value">{paymentInfo.totalGuests}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-payment-container">
      <div className="payment-card">
        {/* Header */}
        <div className="payment-header">
          {paymentInfo?.restaurantLogo && (
            <img
              src={paymentInfo.restaurantLogo}
              alt={paymentInfo.restaurantName}
              className="restaurant-logo"
            />
          )}
          <h1>{paymentInfo?.restaurantName}</h1>
          <p className="reservation-for">Reservation for {paymentInfo?.guestName}</p>
        </div>

        {/* Reservation Details */}
        <div className="reservation-details">
          <div className="detail-item">
            <i className="bi bi-calendar-event"></i>
            <span>{formatDate(paymentInfo?.reservationDate)}</span>
          </div>
          <div className="detail-item">
            <i className="bi bi-clock"></i>
            <span>{formatTime(paymentInfo?.reservationTime)}</span>
          </div>
          <div className="detail-item">
            <i className="bi bi-people"></i>
            <span>{paymentInfo?.totalGuests} guests</span>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="payment-progress">
          <h4>Payment Progress</h4>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${((paymentInfo?.totalPaidCents || 0) / (paymentInfo?.totalRequiredCents || 1)) * 100}%`,
              }}
            />
          </div>
          <div className="progress-stats">
            {!isPerReservation && (
              <span>
                <strong>{paymentInfo?.paidGuestCount}</strong> of{' '}
                <strong>{paymentInfo?.totalGuests}</strong> guests paid
              </span>
            )}
            <span>
              EGP {formatAmount(paymentInfo?.totalPaidCents || 0)} / EGP{' '}
              {formatAmount(paymentInfo?.totalRequiredCents || 0)}
            </span>
          </div>
        </div>

        {/* Confirmation Hint */}
        <div className="payment-hint">
          <i className="bi bi-info-circle"></i>
          <span>Just a heads up — your reservation will be confirmed once the full payment is complete. Thank you!</span>
        </div>

        {/* Payment History */}
        {paymentInfo?.payments?.length > 0 && (
          <div className="payment-history">
            <h4>Payment History</h4>
            <div className="history-list">
              {paymentInfo.payments
                .filter((p) => p.status === 'paid')
                .map((payment) => (
                  <div key={payment.id} className="history-item">
                    <div className="history-info">
                      <span className="payer-name">
                        {payment.payerName || 'Guest'}
                      </span>
                      <span className="guest-count">
                        {payment.guestCount} guest{payment.guestCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="amount">EGP {formatAmount(payment.amountCents)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Pay Form - Per Reservation: Simple pay button */}
        {isPerReservation && paymentInfo?.remainingAmountCents > 0 && (
          <div className="pay-form">
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Total */}
            <div className="total-section">
              <div className="total-amount">
                <span>Total:</span>
                <span className="amount">EGP {formatAmount(paymentInfo.remainingAmountCents)}</span>
              </div>
            </div>

            {/* Pay Button */}
            <button
              className="btn btn-primary btn-pay"
              onClick={handlePayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-credit-card me-2"></i>
                  Pay EGP {formatAmount(paymentInfo.remainingAmountCents)}
                </>
              )}
            </button>
          </div>
        )}

        {/* Pay Form - Per Guest: Full form with guest selector and payer info */}
        {!isPerReservation && paymentInfo?.remainingGuests > 0 && (
          <div className="pay-form">
            <h4>Make a Payment</h4>

            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Guest Count Selector */}
            <div className="form-group">
              <label>Number of Guests to Pay For</label>
              <div className="guest-selector">
                {[...Array(paymentInfo.remainingGuests)].map((_, index) => (
                  <button
                    key={index + 1}
                    type="button"
                    className={`guest-count-btn ${
                      selectedGuestCount === index + 1 ? 'active' : ''
                    }`}
                    onClick={() => setSelectedGuestCount(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Payer Info */}
            <div className="form-group">
              <label>Your Name (Optional)</label>
              <input
                type="text"
                className="form-control"
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>

            <div className="form-group">
              <label>Your Email (Optional)</label>
              <input
                type="email"
                className="form-control"
                value={payerEmail}
                onChange={(e) => setPayerEmail(e.target.value)}
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label>Your Phone (Optional)</label>
              <input
                type="tel"
                className="form-control"
                value={payerPhone}
                onChange={(e) => setPayerPhone(e.target.value)}
                placeholder="Enter your phone"
              />
            </div>

            {/* Total */}
            <div className="total-section">
              <div className="total-breakdown">
                <span>
                  {selectedGuestCount} guest{selectedGuestCount !== 1 ? 's' : ''} × EGP{' '}
                  {formatAmount(paymentInfo.pricePerGuestCents)}
                </span>
              </div>
              <div className="total-amount">
                <span>Total:</span>
                <span className="amount">EGP {formatAmount(calculateTotal())}</span>
              </div>
            </div>

            {/* Pay Button */}
            <button
              className="btn btn-primary btn-pay"
              onClick={handlePayment}
              disabled={processing || selectedGuestCount < 1}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-credit-card me-2"></i>
                  Pay EGP {formatAmount(calculateTotal())}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestPayment;
